import { useFlags } from 'launchdarkly-react-client-sdk';

import { useDerivedNetworkContext } from '@/context';
import { useReleaseFlag } from '@/hooks';
import { useFileFetcher } from '@/services/queryUtils';
import type { DateRange, ShowFilter } from '@/types';
import { downloadFile } from '@/utils/dataDownloadUtils';
import { getDataAvailability } from './useIsBeforeDataAvailability';

/**
 * Calls the server-side location export endpoint and triggers a file download.
 */
const useLocationCsvExport = () => {
  const fetchFile = useFileFetcher();
  const { selectedNetwork } = useDerivedNetworkContext();
  const { showAudienceSocialViews } = useFlags<{ showAudienceSocialViews?: boolean }>();
  const shouldExcludeSocials = showAudienceSocialViews !== true;
  const flag = useReleaseFlag('audience_page_backfill');

  const handleCSVExport = async (
    dateRange: DateRange | undefined,
    selectedShow: ShowFilter
  ): Promise<void> => {
    if (!dateRange) return;

    const { hideContent, queryFrom } = getDataAvailability(
      'location',
      dateRange.from,
      dateRange.to
    );
    if (hideContent) return;

    const queryParams = new URLSearchParams({
      from: queryFrom,
      to: dateRange.to,
    });
    if (selectedShow && selectedShow !== 'all') {
      queryParams.append('showIds', selectedShow);
    }
    if (shouldExcludeSocials) {
      queryParams.append('excludeSocials', String(shouldExcludeSocials));
    }
    if (flag) {
      queryParams.append('flag', flag);
    }

    const path = `/v2/networks/${selectedNetwork.id}/audience/location-plays/export?${queryParams.toString()}`;
    const { blob, fileName } = await fetchFile(path);

    downloadFile(blob, fileName);
  };

  return { handleCSVExport };
};

export default useLocationCsvExport;
